import { Calendar } from "components/generic/Calendar";
import { Button, Col, FormSelect, Row } from "components/primitives";
import { Slider, Switch } from "components/settings/generic/ToggleSetting";
import { useConfirm } from "contexts/ConfirmContext";
import { usePropChangedEffect } from "hooks/propChangedEffect";
import { FC, useCallback, useEffect, useState } from "react";
import Form from "react-bootstrap/esm/Form";
import DatePicker, { DateObject } from "react-multi-date-picker";
import styled from "styled-components";
import { getFullDateString } from "util/getDateString";
import { getDaysInRange } from "util/getDaysInRange";
import { v4 as uuidv4 } from "uuid";

import { CalendarDateFormats } from "../Calendar/CalendarDateFormats";
import { AssignedActivities } from "./AssignedActivities";
import { ScheduleItemData } from "./interfaces";
import { SavedSchedule } from "./OrganiseScheduleSetting";
import { SavedActivities } from "./SavedActivities";
import { SavedSchedulesModal } from "./SavedSchedulesModal";

interface Props {
  savedActivities: ScheduleItemData[];
  savedSchedules: SavedSchedule[];
  onSubmit: (
    assignedSchedule: { [date: string]: SavedSchedule },
    newSavedSchedules: SavedSchedule[],
  ) => void;
  saveNewActivities: (newSavedActivities: ScheduleItemData[]) => void;
  assignedSchedules: { [date: string]: SavedSchedule };
}

export const CalendarViewSetting: FC<Props> = ({
  savedActivities,
  savedSchedules,
  assignedSchedules,
  onSubmit,
  saveNewActivities,
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [month, setMonth] = useState(selectedDate.getMonth());
  const [year, setYear] = useState(selectedDate.getFullYear());
  const [newAssignedSchedules, setNewAssignedSchedules] = useState(assignedSchedules);
  const [newSavedSchedules, setNewSavedSchedules] = useState(savedSchedules);
  const [showSavedSchedulesModal, setShowSavedSchedulesModal] = useState(false);

  const [activeSchedule, setActiveSchedule] = useState<SavedSchedule | undefined>(
    assignedSchedules[getFullDateString(selectedDate)],
  );
  const [isRecurringSchedule, setIsRecurringSchedule] = useState(
    activeSchedule?.isRecurring ?? false,
  );
  const [recurring, setRecurring] = useState<"Weekly" | "Fortnightly" | "Specific days">(
    activeSchedule?.recurring ?? "Weekly",
  );
  const [specificDates, setSpecificDates] = useState<DateObject | DateObject[] | null>(
    activeSchedule?.specificDates ?? new DateObject(selectedDate),
  );
  const [scheduleName, setScheduleName] = useState(activeSchedule?.name ?? "");
  const [startDate, setStartDate] = useState<DateObject | DateObject[] | null>(
    activeSchedule?.startDate ?? new DateObject(selectedDate),
  );
  const [endDate, setEndDate] = useState<DateObject | DateObject[] | null>(
    activeSchedule?.endDate ?? null,
  );
  const confirm = useConfirm();
  const getAssignedActivities = useCallback(
    (date: Date) => {
      const dateString = getFullDateString(date);
      if (dateString in newAssignedSchedules) {
        return newAssignedSchedules[dateString].schedule;
      }
      return [];
    },
    [newAssignedSchedules],
  );

  const [assignedActivities, setAssignedActivities] = useState<ScheduleItemData[]>(
    getAssignedActivities(selectedDate),
  );

  useEffect(() => {
    const newActiveSchedule = newAssignedSchedules[getFullDateString(selectedDate)];
    setActiveSchedule(newActiveSchedule);
    setIsRecurringSchedule(newActiveSchedule?.isRecurring ?? false);
    setRecurring(newActiveSchedule?.recurring ?? "Weekly");
    setSpecificDates(newActiveSchedule?.specificDates ?? new DateObject(selectedDate));
    setScheduleName(newActiveSchedule?.name ?? "");
    setEndDate(newActiveSchedule?.endDate ?? null);
    setStartDate(newActiveSchedule?.startDate ?? new DateObject(selectedDate));
    setAssignedActivities(getAssignedActivities(selectedDate));
    setMonth(selectedDate.getMonth());
    setYear(selectedDate.getFullYear());
  }, [selectedDate, newAssignedSchedules, getAssignedActivities]);

  useEffect(() => {
    setNewAssignedSchedules(assignedSchedules);
  }, [assignedSchedules]);

  const addItemToSchedule = (id: string) => {
    if (assignedActivities.find((a) => a.id === id)) {
      return;
    }
    setAssignedActivities((prevAssignedActivities) => {
      const newActivity = savedActivities.find((a) => a.id === id);
      if (newActivity) {
        return [...prevAssignedActivities, newActivity];
      }
      return prevAssignedActivities;
    });
  };

  usePropChangedEffect(() => {
    monthChanged(selectedDate.getMonth(), selectedDate.getFullYear());
  }, [selectedDate.getMonth(), selectedDate.getFullYear()]);

  const convertToDateStrings = (value: Date | DateObject | DateObject[] | null) => {
    if (Array.isArray(value)) {
      return value.map((date) => (date as DateObject).format("YYYY-MM-DD"));
    } else if (value instanceof DateObject) {
      return [(value as DateObject).format("YYYY-MM-DD")];
    } else if (value instanceof Date) {
      return [getFullDateString(value as Date)];
    } else if (typeof value === "number") {
      return [getFullDateString(new Date(value))];
    } else {
      return [];
    }
  };

  const getRecurringDates = (
    date: Date,
    endDateString: string,
    recurring: "Weekly" | "Fortnightly" | "Specific days",
  ) => {
    if (!recurring) {
      return [];
    }
    if (recurring === "Specific days") {
      return convertToDateStrings(specificDates);
    }
    return getDaysInRange(date.toDateString(), endDateString, recurring);
  };

  const getUniqueScheduleIds = (schedules: { [date: string]: SavedSchedule }) => {
    const ids = Object.values(schedules).map((schedule) => schedule.id);
    return Array.from(new Set(ids));
  };

  const handleSaveClick = async () => {
    let newSchedules = [...newSavedSchedules];
    const endDateString = convertToDateStrings(endDate)[0];
    const startDateDate = new Date(convertToDateStrings(startDate)[0]);
    const dates: string[] = isRecurringSchedule
      ? getRecurringDates(startDateDate, endDateString, recurring)
      : [getFullDateString(selectedDate)];
    const overriddenDates = [];
    for (let date of dates) {
      if (date in newAssignedSchedules) {
        overriddenDates.push(date);
      }
    }
    if (overriddenDates.length > 0) {
      const confirmed = await confirm({
        title: "Info",
        message: "You are overwriting the existing schedule. Are you sure you want to proceed?",
        confirmText: "Ok",
        cancelText: "Cancel",
      });

      if (!confirmed) {
        return;
      }

      // if (!window.confirm("You are overwriting the existing schedule. Are you sure you want to proceed?")) {
      //   return;
      // }
    }
    const activeId = activeSchedule?.id ?? uuidv4();
    const schedule: SavedSchedule = {
      id: activeId,
      schedule: assignedActivities,
      name: scheduleName,
      isRecurring: isRecurringSchedule,
      recurring: recurring,
      startDate: startDate,
      specificDates: specificDates,
      endDate: endDate,
    };

    let newData: { [date: string]: SavedSchedule } = { ...newAssignedSchedules };
    newData = Object.fromEntries(
      Object.entries(newData).filter(([date, schedule]) => schedule.id !== activeId),
    );
    for (let date of dates) {
      newData[date] = schedule;
    }
    newData = cleanAssignedSchedules(newData);
    setNewAssignedSchedules(newData);

    const assignedScheduleIDs = getUniqueScheduleIds(newData);
    newSchedules = newSchedules.filter((schedule) => assignedScheduleIDs.includes(schedule.id));

    if (activeSchedule) {
      newSchedules = newSchedules.filter((s) => s.id !== activeSchedule.id);
    }
    newSchedules.push(schedule);
    setNewSavedSchedules(newSchedules);
    onSubmit(newData, newSchedules);
  };

  const handleDeleteClick = async () => {
    const confirmed = await confirm({
      title: "Info",
      message: "Are you sure you want to delete this schedule?",
      confirmText: "Ok",
      cancelText: "Cancel",
    });

    if (!confirmed) {
      return;
    }
    // if (!window.confirm("Are you sure you want to delete this schedule?")) {
    //   return;
    // }
    const startDateDate = new Date(convertToDateStrings(startDate)[0]);
    const endDateString = convertToDateStrings(endDate)[0];
    const dates: string[] = isRecurringSchedule
      ? getRecurringDates(startDateDate, endDateString, recurring)
      : [getFullDateString(selectedDate)];
    let newData: { [date: string]: SavedSchedule } = { ...newAssignedSchedules };
    for (let date of dates) {
      delete newData[date];
    }
    newData = cleanAssignedSchedules(newData);
    setNewAssignedSchedules(newData);

    let newSchedules = [...newSavedSchedules];
    if (activeSchedule) {
      newSchedules = newSchedules.filter((s) => s.id !== activeSchedule.id);
    }
    setNewSavedSchedules(newSchedules);
    onSubmit(newData, newSchedules);
  };

  function monthDiff(d1: Date, d2: Date) {
    var months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    return months <= 0 ? 0 : months;
  }

  const monthChanged = (month: number, year: number) => {
    setMonth(month);
    setYear(year);
  };

  // Remove any assigned schedules older than 12 months
  const cleanAssignedSchedules = (schedules: { [date: string]: SavedSchedule }) => {
    const cleanSchedules = { ...schedules };
    for (let key of Object.keys(schedules)) {
      const scheduleDate = new Date(key);
      const today = new Date();
      if (monthDiff(scheduleDate, today) > 12) {
        delete cleanSchedules[key];
      }
    }

    return cleanSchedules;
  };

  const updateActivityOrder = (data: ScheduleItemData[]) => {
    setAssignedActivities(data);
  };

  const changeActiveSchedule = (schedule: SavedSchedule) => {
    if (!schedule.startDate) {
      return;
    }
    let startDate = undefined;
    if (schedule.startDate instanceof DateObject) {
      startDate = new Date((schedule.startDate as DateObject).format("YYYY-MM-DD"));
    } else if (typeof schedule.startDate === "number") {
      startDate = new Date(schedule.startDate);
    }
    if (typeof startDate !== "undefined") {
      setSelectedDate(startDate);
    }
  };

  return (
    <OuterContainer>
      <InnerContainer>
        <CalendarContainer>
          <Calendar
            selectedDate={selectedDate}
            month={month}
            year={year}
            startOfWeek="Monday"
            colourPrevDays={true}
            dateSelected={setSelectedDate}
            monthChanged={monthChanged}
            highlightDates={Object.keys(newAssignedSchedules).map(
              (dateString) => new Date(dateString),
            )}
          />
          <SelectedDate>{CalendarDateFormats["Tuesday, 24 April 2023"](selectedDate)}</SelectedDate>
        </CalendarContainer>
        <AssignedActivities
          selectedDate={selectedDate}
          assignedActivities={assignedActivities}
          updateActivityOrder={updateActivityOrder}
          setAssignedActivities={setAssignedActivities}
          activeSchedule={activeSchedule}
        />
        <SavedActivities
          savedActivities={savedActivities}
          onSubmit={saveNewActivities}
          onAddItemToSchedule={addItemToSchedule}
        />
        <ToggleContainer>
          <p>Is recurring?</p>
          <ToggleInner>
            <Switch>
              <Form.Control
                checked={isRecurringSchedule}
                type="checkbox"
                onChange={() => setIsRecurringSchedule(!isRecurringSchedule)}
              />
              <Slider />
            </Switch>
            <span>{isRecurringSchedule ? "Enabled" : "Disabled"}</span>
          </ToggleInner>
        </ToggleContainer>
        {isRecurringSchedule && (
          <FormGroup>
            <Form.Label>Recurring</Form.Label>
            <FormSelect onChange={(e: any) => setRecurring(e.target.value)} value={recurring}>
              {["Weekly", "Fortnightly", "Specific days"].map((option, index) => {
                return (
                  <option key={index} value={option}>
                    {option}
                  </option>
                );
              })}
            </FormSelect>
          </FormGroup>
        )}
        {isRecurringSchedule && (recurring === "Weekly" || recurring === "Fortnightly") && (
          <DatePickerCol>
            <Form.Label>Start date</Form.Label>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              format="DD/MM/YYYY"
              minDate={new Date()}
            />
          </DatePickerCol>
        )}
        {isRecurringSchedule && (recurring === "Weekly" || recurring === "Fortnightly") && (
          <DatePickerCol>
            <Form.Label>End date</Form.Label>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              format="DD/MM/YYYY"
              minDate={startDate as DateObject}
            />
          </DatePickerCol>
        )}
        {isRecurringSchedule && recurring === "Specific days" && (
          <DatePickerCol>
            <Form.Label>Select specific days</Form.Label>
            <DatePicker
              value={specificDates}
              onChange={setSpecificDates}
              multiple={true}
              format="DD/MM/YYYY"
              minDate={new Date()}
            />
          </DatePickerCol>
        )}
        <FormGroup>
          <Form.Label>
            Schedule name <p style={{ fontWeight: 400 }}>(Max characters: 20)</p>
          </Form.Label>
          <Form.Control
            type="text"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            maxLength={20}
          />
        </FormGroup>
      </InnerContainer>
      <ActionButton
        disabled={
          assignedActivities.length < 1 ||
          (isRecurringSchedule && recurring !== "Specific days" && !endDate) ||
          scheduleName.length < 1
        }
        onClick={handleSaveClick}
        title={
          savedSchedules.length >= 20
            ? "You can't save more than 20 schedules at a time"
            : "Save a schedule"
        }
      >
        Save schedule
      </ActionButton>
      {activeSchedule && (
        <ActionButtonDelete
          disabled={!activeSchedule}
          onClick={handleDeleteClick}
          title={"Delete this schedule"}
        >
          Delete schedule
        </ActionButtonDelete>
      )}
      {newSavedSchedules.length > 0 && (
        <ActionButtonTwo
          onClick={() => setShowSavedSchedulesModal((prev) => !prev)}
          title={"Open saved schedules"}
        >
          Open Saved Schedules
        </ActionButtonTwo>
      )}
      {showSavedSchedulesModal && (
        <SavedSchedulesModal
          savedSchedules={newSavedSchedules}
          hasClosed={() => setShowSavedSchedulesModal(false)}
          changeActiveSchedule={changeActiveSchedule}
        />
      )}
    </OuterContainer>
  );
};

const ActionButton = styled(Button)`
  background-color: var(--primary);
  box-shadow: none;
  font-weight: 600;
  font-size: 12pt;
  padding: 10px;
`;

const ActionButtonTwo = styled(Button)`
  background-color: var(--primary);
  box-shadow: none;
  font-weight: 600;
  font-size: 12pt;
  padding: 10px;
  margin-top: 16px;
`;

const ActionButtonDelete = styled(ActionButton)`
  background-color: var(--danger);
  margin-top: 16px;
`;

const OuterContainer = styled(Col)``;

const ToggleContainer = styled(Col)`
  margin-bottom: 35px;

  p {
    margin-bottom: 16px;
    font-weight: 600;
    font-size: 12pt;
  }
`;

const ToggleInner = styled(Row)`
  align-items: center;
  gap: 10px;

  span {
    color: var(--gray);
  }
`;

const InnerContainer = styled(Col)``;

const CalendarContainer = styled(Col)`
  flex: 0.6;
  margin-bottom: 35px;
`;

const DatePickerCol = styled(Col)`
  .rmdp-input {
    height: 38px;
    padding: 0.375rem 0.75rem;
    color: #495057;
    margin-bottom: 35px;
    width: 100%;
    box-shadow: rgb(0, 9, 43) 0px 0px 3px 0px;
  }

  .form-label {
    font-weight: 600;
    font-size: 12pt;
  }
`;

const FormGroup = styled(Form.Group)`
  margin-bottom: 35px;

  .form-label {
    font-weight: 600;
    font-size: 12pt;
    margin-bottom: 1rem;
  }
`;

const SelectedDate = styled.p`
  font-weight: 600;
  font-size: 4cqw;
  width: 100%;
  text-align: center;
  margin: auto;
  padding-top: 10px;
`;
